import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import apiService from '../services/api';
import { MfaEnrollResponse } from '../types/api';
import Logger from '../utils/logger';
import Logo from '../components/Logo';
import { Button, Input } from '../components/ui';

/**
 * Voluntary MFA opt-in. Reachable from the profile menu.
 *
 * Flow:
 *   1. POST /auth/mfa/enroll — receives enrollment_token + QR + backup_codes
 *   2. User scans QR with their authenticator and types the first TOTP code
 *   3. POST /auth/mfa/verify-enroll — activates MFA on the user, response
 *      indicates how many backup codes remain (10 fresh)
 *   4. We surface the backup codes once with a download option, then route
 *      back to the profile page on confirmation.
 */
const MfaEnrollPage: React.FC = () => {
  const { t } = (useTranslation as any)('auth');
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState<MfaEnrollResponse | null>(null);
  const [code, setCode] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .startMfaEnroll()
      .then((data) => {
        if (!cancelled) setEnrollment(data);
      })
      .catch((err) => {
        Logger.error('MFA enroll failed', err);
        if (!cancelled) setError(t('mfa.enrollFailed'));
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enrollment) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiService.verifyMfaEnroll(enrollment.enrollment_token, code.trim());
      setConfirmed(true);
    } catch (err) {
      Logger.error('MFA enroll verify failed', err);
      setError(t('mfa.invalidCode'));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCodes = () => {
    if (!enrollment) return;
    const blob = new Blob([enrollment.backup_codes.join('\n') + '\n'], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nubiecloud-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!enrollment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-0">
        <p className="text-text-secondary">
          {error ?? t('mfa.enrollLoading')}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo size="large" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-text-primary">
          {t('mfa.enrollTitle')}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl">
        <div className="bg-surface-2 py-8 px-6 shadow-card sm:rounded-card space-y-6">
          {!confirmed ? (
            <>
              <p className="text-sm text-text-secondary">{t('mfa.enrollIntro')}</p>
              <div className="flex justify-center">
                <img
                  src={enrollment.qr_data_url}
                  alt="MFA QR code"
                  className="h-56 w-56 rounded border border-border-1"
                />
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer text-text-secondary">
                  {t('mfa.manualUri')}
                </summary>
                <code className="mt-2 block break-all rounded bg-surface-1 p-3 text-xs text-text-primary">
                  {enrollment.otpauth_uri}
                </code>
              </details>

              <form onSubmit={onConfirm} className="space-y-4">
                <Input
                  id="enroll-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  label={t('mfa.codeLabel') as string}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  error={error ?? undefined}
                  className="text-center tracking-widest text-lg font-mono"
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={submitting}
                  disabled={submitting || code.length !== 6}
                  className="w-full"
                >
                  {t('mfa.enrollConfirm')}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-text-primary">
                {t('mfa.backupCodesTitle')}
              </h3>
              <p className="text-sm text-text-secondary">{t('mfa.backupCodesIntro')}</p>
              <ul className="grid grid-cols-2 gap-2 rounded border border-border-1 bg-surface-1 p-3 font-mono text-sm">
                {enrollment.backup_codes.map((c) => (
                  <li key={c} className="text-center tracking-widest text-text-primary">
                    {c}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="secondary"
                  size="md"
                  onClick={downloadCodes}
                  className="w-full sm:w-auto"
                >
                  {t('mfa.downloadCodes')}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={() => {
                    toast.success(t('mfa.enrolled'));
                    navigate('/profile');
                  }}
                  className="w-full sm:w-auto"
                >
                  {t('mfa.savedDone')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MfaEnrollPage;
